/**
 * `<SearchBox>` — custom-styled input row with leading icon, transparent input,
 * optional clear button. Border lights up to --primary on focus-within.
 *
 * Always use this for primary query inputs (search screen, chat composer) —
 * not the bare <input>.
 */

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from '@/components/Icon';

interface SearchBoxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** The controlled value. */
  value: string;
  onChange: (next: string) => void;
  /** Leading icon name. Default `'search'`. */
  icon?: IconName;
  /** Click handler for the clear (×) button. Showing it implies a clear action. */
  onClear?: () => void;
  /** Optional trailing content (e.g. a SegmentedControl + Search button). */
  trailing?: ReactNode;
  /** Wrapper className for layout adjustments at call sites. */
  wrapperClassName?: string;
}

export const SearchBox = forwardRef<HTMLInputElement, SearchBoxProps>(function SearchBox(
  { value, onChange, icon = 'search', onClear, trailing, wrapperClassName = '', placeholder, ...rest },
  ref,
) {
  return (
    <div className={`searchbox ${wrapperClassName}`.trim()}>
      <Icon name={icon} size={16} className="searchbox-icon" />
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        {...rest}
      />
      {onClear && value.length > 0 && (
        <button
          type="button"
          className="searchbox-clear"
          aria-label="Clear"
          onClick={onClear}
        >
          <Icon name="x" size={14} />
        </button>
      )}
      {trailing}
    </div>
  );
});
